/**
 * @fileoverview RFQ Decline Management Hook
 * 
 * Provides functionality for vendors to decline RFQs they are not interested in
 * bidding on. When a vendor declines an RFQ, the decline is recorded with a reason
 * and optional notes, and the RFQ will no longer appear in their active matches.
 * 
 * Decline Reasons:
 * - not_interested: Vendor is not interested in this type of work
 * - too_busy: Vendor has too much current workload
 * - out_of_capability: Job requirements exceed vendor capabilities
 * - budget_too_low: Estimated budget is insufficient
 * - timeline_too_short: Delivery timeline cannot be met
 * - geographic_constraints: Location makes delivery impractical
 * - other: Other reason (requires notes)
 * 
 * Business Logic:
 * - Declines are recorded per vendor-RFQ pair
 * - Once declined, the RFQ is hidden from vendor's dashboard
 * - Declines help improve matching algorithm over time
 * - Clients cannot see which vendors declined or why
 * - Declines are permanent (no undecline functionality)
 * 
 * Key Features:
 * - Record RFQ decline with reason
 * - Check if vendor has already declined an RFQ
 * - Loading state management
 * - Authentication integration
 * 
 * @module useRFQDeclines
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook for managing RFQ declines
 * 
 * Provides functions to decline RFQs and check decline status. Declines are
 * recorded in the rfq_declines table with the vendor's user ID, decline reason,
 * and optional notes.
 * 
 * Decline Flow:
 * 1. Vendor views RFQ match in their dashboard
 * 2. Vendor clicks "Not Interested" or similar action
 * 3. System prompts for decline reason (required)
 * 4. System optionally prompts for additional notes
 * 5. Decline is recorded in database
 * 6. RFQ is removed from vendor's active matches
 * 
 * @returns Object with decline functions and loading state
 * 
 * @example
 * ```tsx
 * function RFQCard({ rfq }) {
 *   const { declineRFQ, hasDeclined, loading } = useRFQDeclines();
 *   const [declined, setDeclined] = useState(false);
 * 
 *   useEffect(() => {
 *     hasDeclined(rfq.id).then(setDeclined);
 *   }, [rfq.id]);
 * 
 *   const handleDecline = async () => {
 *     try {
 *       await declineRFQ(
 *         rfq.id,
 *         'too_busy',
 *         'Current workload is at capacity for next 3 months'
 *       );
 *       setDeclined(true);
 *     } catch (error) {
 *       console.error('Failed to decline RFQ:', error);
 *     }
 *   };
 * 
 *   if (declined) return null;  // Hide declined RFQs
 * 
 *   return (
 *     <Card>
 *       <CardHeader>{rfq.title}</CardHeader>
 *       <CardFooter>
 *         <Button onClick={handleDecline} disabled={loading}>
 *           Not Interested
 *         </Button>
 *       </CardFooter>
 *     </Card>
 *   );
 * }
 * ```
 */
export const useRFQDeclines = () => {
  const [loading, setLoading] = useState(false);

  /**
   * Records that a vendor has declined an RFQ
   * 
   * Inserts a record into rfq_declines table linking the vendor to the RFQ
   * with the specified decline reason and optional notes. Once declined, the
   * RFQ will no longer appear in the vendor's active matches.
   * 
   * The decline reason is used for analytics and improving the matching algorithm.
   * Notes provide additional context for internal analysis but are not shown to clients.
   * 
   * @param rfqId - The UUID of the RFQ being declined
   * @param reason - Standardized decline reason code
   * @param notes - Optional additional explanation (recommended for 'other' reason)
   * @throws Error if user is not authenticated or database insert fails
   * 
   * @example
   * ```typescript
   * // Simple decline with just reason
   * await declineRFQ('rfq-uuid', 'not_interested');
   * 
   * // Decline with detailed notes
   * await declineRFQ(
   *   'rfq-uuid',
   *   'out_of_capability',
   *   'We do not have 5-axis CNC machines required for this job'
   * );
   * ```
   */
  const declineRFQ = async (rfqId: string, reason: string, notes?: string) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.from('rfq_declines').insert({
        rfq_id: rfqId,
        vendor_user_id: user.id,
        decline_reason: reason,
        decline_notes: notes || null,
      });

      if (error) throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Checks if the current vendor has already declined a specific RFQ
   * 
   * Queries the rfq_declines table to determine if a decline record exists for
   * the current user and the specified RFQ. Used to hide already-declined RFQs
   * from the vendor's dashboard.
   * 
   * Error Handling:
   * - Returns false if user is not authenticated
   * - Returns false if database query fails
   * - Errors are logged but do not throw
   * 
   * @param rfqId - The UUID of the RFQ to check
   * @returns Promise resolving to true if vendor has declined, false otherwise
   * 
   * @example
   * ```typescript
   * // Check before showing RFQ
   * const declined = await hasDeclined('rfq-uuid');
   * if (declined) {
   *   // Don't show this RFQ in list
   *   return null;
   * }
   * 
   * // Use with useEffect to update UI
   * useEffect(() => {
   *   hasDeclined(rfqId).then(isDeclined => {
   *     if (isDeclined) {
   *       removeFromList(rfqId);
   *     }
   *   });
   * }, [rfqId]);
   * ```
   */
  const hasDeclined = async (rfqId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data } = await supabase
        .from('rfq_declines')
        .select('id')
        .eq('rfq_id', rfqId)
        .eq('vendor_user_id', user.id)
        .maybeSingle();

      return !!data;
    } catch (error) {
      console.error('Error checking decline status:', error);
      return false;
    }
  };

  return { declineRFQ, hasDeclined, loading };
};